import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckBadgeIcon, IdentificationIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import AdminLayout from './AdminLayout';
import CustomerLayout from './CustomerLayout';

const fieldLabel = (value) => value || 'N/A';
const normalizeLookupValue = (value) => value.trim().toLowerCase();

const UniqueIdVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [uniqueId, setUniqueId] = useState('');
  const [vinNumber, setVinNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [record, setRecord] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({ uniqueId: false, vinNumber: false });

  const hasResult = useMemo(() => searched && !loading, [searched, loading]);
  const fromPath = location.state?.from;
  const isAdminReturn =
    fromPath === '/admin' ||
    (typeof fromPath === 'string' && fromPath.startsWith('/admin/'));

  const handleBackToDashboard = () => {
    if (isAdminReturn) {
      const adminReturnPath = typeof fromPath === 'string' && fromPath.startsWith('/admin/') ? fromPath : '/admin';
      navigate(adminReturnPath);
      return;
    }

    navigate(fromPath || '/dashboard');
  };

  const handleVerify = async (event) => {
    event.preventDefault();

    const trimmedUniqueId = uniqueId.trim();
    const trimmedVin = vinNumber.trim();
    const normalizedUniqueId = normalizeLookupValue(uniqueId);
    const normalizedVin = normalizeLookupValue(vinNumber);

    if (!trimmedUniqueId || !trimmedVin) {
      setError('Please enter both Unique ID and VIN.');
      setFieldErrors({
        uniqueId: !trimmedUniqueId,
        vinNumber: !trimmedVin,
      });
      setRecord(null);
      setSearched(true);
      return;
    }

    setLoading(true);
    setError('');
    setFieldErrors({ uniqueId: false, vinNumber: false });
    setRecord(null);
    setSearched(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/verify-unique-id-vin`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uniqueId: trimmedUniqueId,
            vinNumber: trimmedVin,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || result.error) {
        throw result;
      }

      const matchedRecord = result.record &&
        normalizeLookupValue(result.record.customer_unique_id || '') === normalizedUniqueId &&
        normalizeLookupValue(result.record.vin_number || '') === normalizedVin
        ? result.record
        : null;

      setRecord(result.found ? matchedRecord : null);
    } catch (err) {
      console.error('Unique ID verification failed:', err);
      setError('Unable to verify the record right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const Layout = isAdminReturn ? AdminLayout : CustomerLayout;

  return (
    <Layout
      activeItemId="verify-uid"
      title="Unique ID Verification"
      subtitle="Verify Unique ID and VIN against our record."
      contentClassName="mx-auto w-full"
      footerContainerClassName="mt-16 lg:mt-24"
    >
      <div className="grid items-start gap-6 pb-10 xl:grid-cols-[minmax(0,1.7fr)_minmax(320px,0.9fr)] xl:pb-16">
        <section className="order-2 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm shadow-slate-200/60 xl:order-1">
          <div className="px-5 pt-5 pb-3 sm:px-6">
            <h2 className="flex items-center gap-2 text-md font-medium text-gray-800 uppercase tracking-[0.08em]">
              <IdentificationIcon className="h-5 w-5 text-blue-600" />
              Verification Details
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Enter both values exactly as issued to verify the record.
            </p>
          </div>

          <div className="border-t border-slate-200 px-5 py-5 sm:px-6 sm:py-6">
            <form onSubmit={handleVerify} className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Unique ID</label>
                  <input
                    type="text"
                    value={uniqueId}
                    onChange={(event) => setUniqueId(event.target.value)}
                    placeholder="Enter Unique ID"
                    className={`w-full rounded-[1rem] border bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:ring-2 focus:ring-blue-100 ${fieldErrors.uniqueId ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'}`}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">VIN</label>
                  <input
                    type="text"
                    value={vinNumber}
                    onChange={(event) => setVinNumber(event.target.value)}
                    placeholder="Enter VIN"
                    className={`w-full rounded-[1rem] border bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:ring-2 focus:ring-blue-100 ${fieldErrors.vinNumber ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-blue-500'}`}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center rounded-[0.9rem] bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? 'Verifying...' : 'Verify Records'}
              </button>
            </form>

            <div className="mt-6 space-y-4">
              {error && (
                <div className="rounded-[1rem] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
                  {error}
                </div>
              )}

              {hasResult && !record && !error && (
                <div className="rounded-[1rem] border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 shadow-sm">
                  No results found.
                </div>
              )}

              {record && (
                <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50/80 p-4 shadow-sm shadow-slate-200/60 sm:p-5">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="flex items-center gap-1 text-sm font-semibold text-green-700">
                        <CheckBadgeIcon className="h-4 w-4" />
                        <span>Match found</span>
                      </p>
                      <h3 className="mt-1 text-md font-medium text-gray-900 uppercase tracking-[0.04em]">Record details</h3>
                    </div>
                    <div className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700 ring-1 ring-green-200">
                      Verified
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <Detail label="Unique ID" value={fieldLabel(record.customer_unique_id)} />
                    <Detail label="VIN" value={fieldLabel(record.vin_number)} />
                    <Detail label="State" value={fieldLabel(record.state)} />
                    <Detail label="MRE" value={fieldLabel(record.mr_approval || record.mr_id)} />
                    <Detail label="Build Date" value={fieldLabel(record.build_date)} />
                    <Detail label="AVV No" value={fieldLabel(record.avv_approval_number)} />
                    <Detail label="AVV Email" value={fieldLabel(record.avv_approval_email)} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <aside className="order-1 self-start overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm shadow-slate-200/60 xl:order-2">
          <div className="px-5 py-5 sm:px-6 sm:py-6">
            <div>
              <h2 className="mb-4 flex items-center gap-2 text-md font-medium text-gray-900 uppercase tracking-[0.04em]">
                <InformationCircleIcon className="h-5 w-5 text-blue-600" />
                How it works
              </h2>
            </div>
            <div className="space-y-3 text-sm text-slate-600">
              <div className="rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                1. Enter the Unique ID exactly as issued.
              </div>
              <div className="rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                2. Enter the exact VIN.
              </div>
              <div className="rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                3. Submit to see the matching record details or a not found message.
              </div>
            </div>
          </div>

        </aside>
      </div>
    </Layout>
  );
};

const Detail = ({ label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</div>
    <div className="mt-1 break-words text-sm font-medium text-slate-900">{value}</div>
  </div>
);

export default UniqueIdVerification;